import React, { useEffect, useState } from "react";
import { ArrowDownToLine, Eye, FileText } from "lucide-react";
import { PortfolioFilePreview } from "../components/institution-portfolio/PortfolioOverlay";
import { profileCountries } from "../data/profileCountries";
import { applicable, TermsLink } from "./ApplicationFormFields";
import {
  Answer,
  Field,
  FormResponse,
  FormSnapshot,
  Row,
  Upload,
  formError,
} from "./types";

export function saveAttachment(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function FormSnapshotSummary({
  value,
  loadFile,
}: {
  value: FormSnapshot;
  loadFile?: (file: Upload) => Promise<Blob>;
}) {
  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    mimeType: string;
  }>();
  const [fileError, setFileError] = useState("");
  const [busyFile, setBusyFile] = useState<string>();
  const mounted = React.useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview.url);
    },
    [preview],
  );
  const openFile = async (file: Upload, showPreview: boolean) => {
    if (!loadFile) return;
    setFileError("");
    setBusyFile(file.id);
    try {
      const blob = await loadFile(file);
      if (!mounted.current) return;
      if (showPreview) {
        setPreview({
          url: URL.createObjectURL(blob),
          name: file.filename,
          mimeType: blob.type,
        });
      } else saveAttachment(blob, file.filename);
    } catch (problem) {
      if (mounted.current) setFileError(formError(problem));
    } finally {
      if (mounted.current) setBusyFile(undefined);
    }
  };
  const text = (files: Upload[], field: Field, value?: Answer) => {
    if (value === undefined || value === "") return "—";
    if (field.type === "file") {
      const file = files.find((upload) => upload.id === value);
      if (!file) return "Unavailable";
      return (
        <div className="border rounded p-2 d-flex align-items-center flex-wrap gap-2">
          <FileText
            size={16}
            className="flex-shrink-0 text-muted"
            aria-hidden="true"
          />
          <span className="me-auto text-break">{file.filename}</span>
          {loadFile && (
            <>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                aria-label={`Preview ${file.filename}`}
                disabled={!!busyFile}
                onClick={() => void openFile(file, true)}
              >
                <Eye size={14} className="me-1" aria-hidden="true" />
                Preview
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                aria-label={`Download ${file.filename}`}
                disabled={!!busyFile}
                onClick={() => void openFile(file, false)}
              >
                <ArrowDownToLine
                  size={14}
                  className="me-1"
                  aria-hidden="true"
                />
                Download
              </button>
            </>
          )}
        </div>
      );
    }
    if (field.type === "country")
      return (
        profileCountries.find((country) => country.code === value)?.name ||
        String(value)
      );
    if (typeof value === "boolean") return value ? "Yes" : "No";
    const options = field.options;
    const label = (item: string) =>
      options?.find((option) => option.id === item)?.label || item;
    return Array.isArray(value)
      ? value.map(label).join(", ")
      : label(String(value));
  };
  const render = (files: Upload[], fields: Field[], row: Row) => (
    <dl className="row g-3 mb-0">
      {fields.filter((field) => field.type !== "termsAgreement" || applicable(field, row)).map((field) => (
        <div
          className={
            field.type === "file" || field.type === "textarea"
              ? "col-12"
              : "col-12 col-md-6"
          }
          key={field.id}
        >
          <dt>{field.label}</dt>
          <dd className="mb-0 text-break">
            {field.type === "termsAgreement" ? (
              <>
                {typeof row[field.id] === "boolean" && (
                  <span className="me-2">{text(files, field, row[field.id])}</span>
                )}
                <TermsLink field={field} />
              </>
            ) : text(files, field, row[field.id])}
          </dd>
        </div>
      ))}
    </dl>
  );
  return (
    <>
      {fileError && (
        <p className="alert alert-warning" role="alert">
          {fileError}
        </p>
      )}
      {busyFile && (
        <p className="visually-hidden" role="status">
          Loading attachment…
        </p>
      )}
      {preview && (
        <PortfolioFilePreview
          {...preview}
          centered
          close={() => setPreview(undefined)}
        />
      )}
      <p className="text-muted">
        {value.schema.programmes.find(
          (item) => item.offeringId === value.offeringId,
        )?.label || "Programme unavailable"}{" "}
        · Intake {value.intake || "—"} · Form revision {value.revisionId}
      </p>
      {value.schema.sections.map((section) => (
        <section className="card border-0 shadow-sm mb-3" key={section.id}>
          <div className="card-header bg-light">
            <h3 className="h5 mb-0 fw-semibold">{section.label}</h3>
          </div>
          <div className="card-body">
            {section.repeat
              ? (value.answers[section.id] as Row[] | undefined)?.map(
                  (row, index) => (
                    <div className="mb-2" key={index}>
                      <h4 className="small fw-bold">Entry {index + 1}</h4>
                      {render(value.uploads, section.fields, row)}
                    </div>
                  ),
                )
              : render(value.uploads, section.fields, value.answers as Row)}
          </div>
        </section>
      ))}
    </>
  );
}

export function FormResponseCard({
  id,
  load,
  download,
}: {
  id: number;
  load: (id: number, signal?: AbortSignal) => Promise<FormResponse | null>;
  download: (id: number, fileId: string) => Promise<Blob>;
}) {
  const [data, setData] = useState<FormResponse | null>(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError("");
    void load(id, controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setData(value);
      })
      .catch((problem) => {
        if (!controller.signal.aborted) setError(formError(problem));
      });
    return () => controller.abort();
  }, [id, load, retry]);
  if (!data && !error) return null;
  return (
    <section className="card my-3">
      <div className="card-body">
        {error && (
          <div className="alert alert-warning" role="alert">
            {error}{" "}
            <button
              type="button"
              className="btn btn-link"
              onClick={() => setRetry((value) => value + 1)}
            >
              Retry
            </button>
          </div>
        )}
        {data && (
          <>
            <h2 className="h5">{data.schema.title}</h2>
            <FormSnapshotSummary
              value={data}
              loadFile={(file) => download(id, file.id)}
            />
            {!!data.history.length && (
              <details>
                <summary className="fw-semibold">
                  Submitted versions ({data.history.length})
                </summary>
                {data.history.map((value) => (
                  <details key={value.id} className="mt-2 border rounded p-2">
                    <summary>
                      Submitted {new Date(value.submittedAt).toLocaleString()}
                    </summary>
                    <FormSnapshotSummary
                      value={value}
                      loadFile={(file) => download(id, file.id)}
                    />
                  </details>
                ))}
              </details>
            )}
          </>
        )}
      </div>
    </section>
  );
}
